import React,{useState, useEffect} from 'react'
import {Box, Button} from '@mui/material';
import CheckboxField from '../../../../../../../../component/CheckboxField';
import SetSchedule from './SetSchedule';
import { calculateTime } from '../../../../../../../../utils/calculateTime';
import styled from "@emotion/styled";
import ErrorMsg from '../../../../../../../../component/ErrorMsg';
import { tokens, motion, radius } from '../../../../../../../../theme';

import { pressable } from "../../../../../../../../component/pressableProps";
const SectionLabel = styled(Box)({
    fontSize:"0.9375rem",
    fontWeight: 600,
    color: tokens.text,
    marginBottom: "0.75rem",
});

const panelSx = {
    width: "100%",
    borderRadius: `${radius.card}px`,
    border: `1px solid ${tokens.border}`,
    padding: "1.25rem",
    background: tokens.card,
    marginBottom: "1.25rem",
};

const daysOfWeek = ['Sunday','Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// compact selectable day chip
const DayChip = ({ label, selected, onClick, disabled, tone = tokens.accent }) => (
  <Box
    {...pressable(disabled ? undefined : onClick)}
    sx={{
      textAlign: "center",
      py: "0.5rem",
      borderRadius: "8px",
      fontSize: "0.8125rem",
      fontWeight: selected ? 600 : 500,
      cursor: disabled ? "default" : "pointer",
      userSelect: "none",
      border: `1px solid ${selected ? tone : tokens.border}`,
      background: selected ? `${tone}14` : tokens.card,
      color: selected ? tone : tokens.textMuted,
      opacity: disabled ? 0.6 : 1,
      transition: `border-color 120ms ${motion.easing.standard}, background-color 120ms ${motion.easing.standard}`,
      "&:hover": disabled ? {} : { borderColor: tone },
    }}
  >
    {label.slice(0, 3)}
  </Box>
);

const SetShift = ({shiftData, setShiftData, workingDays, setWorkingDays, holidays, setHolidays, initialDayState, disabled}) => {

    const [displaySetScheduleFields, setDisplaySetScheduleFields] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState();
    const [workingDaysError,setWorkingDaysError]=useState("");
    const [holidaysError,setHolidaysError]=useState("");

      const handleCheckboxChange = (index, type) => {
        setWorkingDaysError('');
        setHolidaysError('');

        const updatedDays = [...shiftData.days];

        if (type === 'working') {
          if (holidays.includes(index)) {
            setWorkingDaysError(`${daysOfWeek[index]} is already set as a holiday.`);
            return;
          }
          setWorkingDays(prev => {
            if (prev.includes(index)) {
                updatedDays[index] = { ...initialDayState };
                return prev.filter(day => day !== index);
            } else {
                updatedDays[index] = { ...updatedDays[index], isHoliday: false };
                return [...prev, index];
            }
          });
        } else {
          if (workingDays.includes(index)) {
            setHolidaysError(`${daysOfWeek[index]} is already set as a working day.`);
            return;
          }
          setHolidays(prev =>
            prev.includes(index) ? prev.filter(day => day !== index) : [...prev, index]
          );
          updatedDays[index] = {...initialDayState};
        }
        setShiftData(prevState => ({ ...prevState, days: updatedDays }));
      };

      useEffect(() => {
        setShiftData(prevState => ({
          ...prevState,
          days: Array(7).fill(initialDayState)
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [shiftData?.doesScheduleVary]);

  const dayGridSx = { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "0.5rem", marginTop: "1rem" };

  return (
    <Box sx={{width:"100%"}}>
        <Box sx={{marginBlock: "1.25rem"}}>
            <SectionLabel>Set Shift Timing</SectionLabel>
            <CheckboxField
                    label="Employees are working with flexible schedules, not restricted to specific hours."
                    checked={shiftData?.flexibleWorkingHours}
                    onChange={(isChecked) => setShiftData((prevData) => ({
                      ...prevData,
                      flexibleWorkingHours: isChecked,
                    }))}
                    labelStyle={{fontSize: "0.9375rem", lineHeight: 1.6, color: tokens.textMuted}}
                    disabled={disabled}
                />
        </Box>

        <Box sx={panelSx}>
                <SectionLabel>Shift Working Days</SectionLabel>
                <CheckboxField
                        label="Work schedule varies on a daily basis."
                        checked={shiftData?.doesScheduleVary}
                        onChange={(isChecked) => setShiftData((prevData) => ({
                          ...prevData,
                          doesScheduleVary: isChecked,
                        }))}
                        labelStyle={{fontSize: "0.875rem", lineHeight: 1.6, color: tokens.textMuted}}
                        disabled={disabled}
                    />
                <Box sx={dayGridSx}>
                {
                    daysOfWeek.map((day, index) => (
                    <Box key={index}>
                        <DayChip
                          label={day}
                          selected={workingDays.includes(index)}
                          onClick={() => handleCheckboxChange(index, "working")}
                          disabled={disabled}
                        />
                        {(shiftData?.doesScheduleVary && workingDays.includes(index)) && (
                        <Button
                            onClick={() => { setSelectedIndex(index); setDisplaySetScheduleFields(true); }}
                            disabled={disabled}
                            sx={{ width: "100%", textTransform: "none", fontWeight: 500, fontSize: "0.8125rem",
                              lineHeight: 1.5, color: tokens.accent, mt: "0.25rem", p: 0, minWidth: 0 }}
                        >
                            {shiftData?.days[index]?.minFullDayHours === 0 && shiftData?.days[index]?.startTime === 0
                            ? "Set Schedule"
                            : (shiftData?.flexibleWorkingHours
                                ? shiftData?.days[index]?.minFullDayHours + " hrs"
                                : calculateTime(shiftData?.days[index]?.startTime) + " - " + calculateTime(shiftData?.days[index]?.endTime))}
                        </Button>
                        )}
                    </Box>
                    ))
                }
                </Box>
                <ErrorMsg>{workingDaysError}</ErrorMsg>
                {
                (shiftData?.doesScheduleVary && displaySetScheduleFields) && (
                    <SetSchedule disabled={disabled} shiftData={shiftData} setShiftData={setShiftData} workingDays={workingDays} selectedIndex={selectedIndex} />
                )
                }
                {
                !shiftData?.doesScheduleVary && (
                    <SetSchedule disabled={disabled} shiftData={shiftData} setShiftData={setShiftData} workingDays={workingDays} selectedIndex={selectedIndex} />
                )
                }
        </Box>

        <Box sx={panelSx}>
                <SectionLabel>Week Off Days</SectionLabel>
                <Box sx={dayGridSx}>
                {daysOfWeek.map((day, index) => (
                  <DayChip
                    key={index}
                    label={day}
                    selected={holidays.includes(index)}
                    onClick={() => handleCheckboxChange(index, "holiday")}
                    disabled={disabled}
                    tone={tokens.danger}
                  />
                ))}
                </Box>
                <ErrorMsg>{holidaysError}</ErrorMsg>
        </Box>
    </Box>
  )
}

export default SetShift
